import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { Rocket, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { useLaunchCommandCenter, useRunLaunchSmoke } from './launch.hooks';

export function LaunchCommandCenterPage() {
  const { permissions } = useAuth();
  const { formatDateTime: formatDate } = useUserPreferences();
  const dashboardQuery = useLaunchCommandCenter();
  const smokeMutation = useRunLaunchSmoke();
  const canAdmin = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const runError = smokeMutation.error instanceof Error ? smokeMutation.error.message : null;

  if (!canAdmin) {
    return (
      <EmptyState
        icon={<Rocket className="h-5 w-5" aria-hidden="true" />}
        title="Launch polish access required"
        description={`Your role needs ${PERMISSIONS.PLATFORM_ADMIN} to view launch readiness.`}
      />
    );
  }

  if (dashboardQuery.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        title="Unable to load launch polish"
        description={
          dashboardQuery.error instanceof Error
            ? dashboardQuery.error.message
            : 'Unknown launch polish error'
        }
        action={
          <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const data = dashboardQuery.data;

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="S37 · Launch Polish"
          title="Launch Polish"
          description="Check packaged accessibility, navigation, test coverage, and release documents. Browser tests run separately in the release pipeline."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Refresh
              </Button>
              <Button
                size="sm"
                disabled={smokeMutation.isPending || !data.permissions.canRunLaunchSmoke}
                onClick={() => {
                  smokeMutation.reset();
                  smokeMutation.mutate();
                }}
              >
                <Rocket className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {smokeMutation.isPending ? 'Checking…' : 'Check launch artifacts'}
              </Button>
            </div>
          }
        />
      }
      toolbar={
        <>
          {runError ? <p className="text-destructive text-sm">{runError}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Checklist items
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{data.summary.itemCount}</p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Categories</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.categoryCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Latest pass rate
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.latestPassRate === null ? '—' : `${data.summary.latestPassRate}%`}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Failures in recent history
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.openFailedFindings}
              </p>
              <p className="text-muted-foreground mt-1 text-xs">
                Failed results among the 40 most recent findings; earlier failures remain visible
                after a later pass.
              </p>
            </Card>
          </div>
        </>
      }
      sections={[
        {
          id: 'launch-checklist',
          label: 'Launch checklist',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Launch checklist</h2>
              </div>
              <ul className="divide-y">
                {data.items.map((item) => (
                  <li
                    key={item.id}
                    className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-muted-foreground text-sm">{item.description}</p>
                    </div>
                    <Badge variant="outline">{item.category}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ),
        },
        {
          id: 'recent-findings',
          label: 'Recent findings',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Recent findings</h2>
              </div>
              {data.recentFindings.length === 0 ? (
                <EmptyState
                  className="border-0"
                  title="No launch findings yet"
                  description="Check launch artifacts to verify required components, test files, and release documents."
                />
              ) : (
                <ul className="divide-y">
                  {data.recentFindings.slice(0, 12).map((finding) => (
                    <li key={finding.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium">{finding.title}</p>
                        <Badge variant={finding.passed ? 'success' : 'critical'}>
                          {finding.passed ? 'PASS' : 'FAIL'}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">{finding.detail}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {finding.runNumber} · {finding.severity}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
        {
          id: 'readiness-check-history',
          label: 'Readiness check history',
          content: (
            <Card className="overflow-hidden">
              <div className="hx-section-header border-b px-3 py-2">
                <h2 className="hx-section-title text-sm font-semibold">Readiness check history</h2>
              </div>
              {data.runs.length === 0 ? (
                <EmptyState
                  className="border-0"
                  title="No readiness checks"
                  description="Audited artifact checks appear here after the first check."
                />
              ) : (
                <ul className="divide-y">
                  {data.runs.map((run) => (
                    <li key={run.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium">{run.runNumber}</p>
                        <Badge variant="outline">{run.status}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">{run.summary}</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {run.findingsPassed}/{run.itemsChecked} passed ·{' '}
                        {formatDate(run.completedAt ?? run.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
      ]}
    />
  );
}
